import { useEffect } from "react";
import { Link } from "react-router-dom";
import ArticleSchema from "@/components/ArticleSchema";
import Breadcrumbs from "@/components/Breadcrumbs";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import { SITE_ORIGIN, setPageMeta, setHreflang } from "@/lib/seo";

const EN_URL = "/blog/14k-vs-16k-dump-trailer";
const ES_URL = "/es/remolque-volteo-14k-vs-16k";

export default function RemolqueVolteo14kVs16kPage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "Tráiler de Volteo 14K vs 16K: Cómo Decidir | Juan The Trailer Guy",
      "¿14K o 16K en un tráiler de volteo? Te explicamos la diferencia en capacidad de carga, uso diario y cuál conviene según el trabajo que haces.",
      ES_URL,
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-background text-foreground">
      <ArticleSchema
        headline="Tráiler de Volteo 14K vs 16K: Cómo Decidir"
        description="Comparación lado a lado de tráileres de volteo 14K y 16K: capacidad de carga, implicaciones de CDL y uso real en el trabajo."
        url={`${SITE_ORIGIN}${ES_URL}`}
        image={`${SITE_ORIGIN}/images/jtg-logo-preview.png`}
      />
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[
              { name: "Inicio", href: "/" },
              { name: "Guías Educativas" },
              { name: "Tráiler de Volteo 14K vs 16K" },
            ]}
          />
          <p className="text-sm uppercase tracking-[0.2em] text-red-400 font-semibold">Tráileres de Volteo</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-black uppercase leading-tight">
            Tráiler de Volteo 14K vs 16K: Cómo Decidir
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-zinc-300">
            ¿No sabes si necesitas un tráiler de volteo (dump trailer) de 14,000 o 16,000 libras? Todo se
            reduce a la capacidad de carga, qué tan seguido la vas a usar, y el tipo de trabajo que haces.
            Aquí te explicamos cómo pensarlo.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="rounded-xl border border-border bg-muted/50 p-6">
          <p className="mb-1 text-sm font-semibold uppercase tracking-widest text-primary">Respuesta Rápida</p>
          <p className="text-lg leading-relaxed text-foreground">
            Una <strong>tráiler de volteo de 14K</strong> es ideal para uso general, trabajos más ligeros y
            cargas ocasionales. Una <strong>tráiler de 16K</strong> es mejor para uso pesado y constante, como
            acarrear tierra, grava y escombro de demolición.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-10 text-lg leading-8 text-muted-foreground">
        <p>
          Una de las preguntas más comunes entre los que compran su primer tráiler es si conviene una de
          14K o una de 16K.
        </p>
        <p className="mt-4">
          La verdad es que ambas son buenas opciones. La decisión correcta depende de qué tan seguido la
          vas a usar y qué tipo de material acarreas en tu día a día.
        </p>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Tráiler de Volteo 14K</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            El tráiler de 14K es una de las configuraciones más comunes en el mercado. Funciona bien para
            contratistas generales, uso personal y trabajo comercial más ligero.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Generalmente más liviana y más económica de mantener que una de mayor capacidad</li>
            <li>• Es más suave con tu troca (menos desgaste en el motor y la transmisión)</li>
            <li>• Buena opción para uso ocasional o moderado</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            Si acarreas escombro, material de techo o cargas ligeras, un tráiler de 14K normalmente es
            suficiente.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Tráiler de Volteo 16K</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            El tráiler de 16K está construido para cargas más pesadas y uso más frecuente. Es la mejor
            opción si constantemente acarreas material denso.
          </p>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• Mayor capacidad de carga (payload)</li>
            <li>• Mejor para tierra, grava y concreto</li>
            <li>• Construida para uso diario o pesado</li>
          </ul>
          <p className="mt-6 text-muted-foreground">
            Si usas tu tráiler como parte de tu negocio todas las semanas, pasar a la 16K tiene sentido.
          </p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">14K vs 16K: Diferencias Clave</h2>
          <ul className="mt-6 space-y-4 text-lg text-muted-foreground">
            <li><strong className="text-foreground">Capacidad:</strong> la 16K soporta cargas más pesadas</li>
            <li><strong className="text-foreground">Costo de propiedad:</strong> la 14K suele ser más económica</li>
            <li><strong className="text-foreground">Uso:</strong> 14K = ocasional, 16K = uso frecuente</li>
            <li><strong className="text-foreground">Durabilidad:</strong> la 16K aguanta mejor el uso rudo con el tiempo</li>
          </ul>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">¿Cuál le Conviene a tu Trabajo?</h2>
          <p className="mt-6 text-lg text-muted-foreground leading-8">
            Si usas tu tráiler de vez en cuando o para trabajos ligeros, una 14K te va a funcionar bien.
          </p>
          <p className="mt-4 text-lg text-muted-foreground leading-8">
            Pero si acarreas material pesado de forma regular o manejas un negocio, vale la pena considerar
            la 16K.
          </p>
          <p className="mt-6 font-semibold text-foreground">
            En caso de duda, planea con un poco más de capacidad de la que crees necesitar, para que no se
            te quede corto el tráiler.
          </p>
        </div>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Sigue Aprendiendo</h2>
          <div className="mt-6 text-primary font-semibold space-y-2">
            <Link to="/es/remolques-de-volteo" className="block hover:underline">→ Tráileres de Volteo Explicadas</Link>
            <Link to="/es/gvwr-capacidad-de-carga" className="block hover:underline">→ GVWR y Capacidad de Carga</Link>
            <Link to="/es/costos-de-tener-un-remolque" className="block hover:underline">→ Costos Reales de Tener un Remolque</Link>
            <Link to="/trailer-payload-calculator" className="block hover:underline">→ Calculadora de GVWR y Carga (en inglés)</Link>
          </div>
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl md:text-5xl font-black uppercase">¿Todavía Decidiendo Entre 14K y 16K?</h2>
          <p className="mt-6 text-lg text-zinc-300">
            Sigue leyendo nuestras guías educativas para entender qué configuración se ajusta mejor a tu
            trabajo.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/es/remolques-de-volteo" className="bg-red-700 px-6 py-3 rounded-md font-bold uppercase hover:bg-red-800">
              Tráileres de Volteo Explicadas
            </Link>
            <Link to="/es/licencia-cdl-remolques" className="border border-white/30 px-6 py-3 rounded-md font-bold uppercase hover:bg-white/10">
              Licencia CDL para Remolques
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
